import { ContentBlockFormValues } from '@/containers/ContentBlocks/types'

/**
 * Client-side staged edits for ONE content block within the page editor.
 * Lives in `PageDetails` (NOT in `ContentBlockFormBody`, which is remounted
 * via `key={blockId}` and would lose this on block switch / panel close), so
 * a draft survives closing the block's panel and reopening a different one,
 * and is only committed (via the existing per-block mutations) when the
 * page-level Save runs.
 */
export interface BlockDraft {
  /**
   * Latest full settings-form snapshot (all fields, all languages'
   * `content_block_profiles` — minus `description`, which this form never
   * touches in the page editor). Replaced wholesale on every settings-form
   * change and used to RE-SEED the form if this block's panel is reopened
   * mid-session, so an earlier uncommitted settings edit is never silently
   * discarded by a later one. `null` until the settings form has been
   * touched at least once for this block.
   */
  formValues: ContentBlockFormValues | null
  /**
   * Per-language NAME overrides staged from the inline title editor
   * (`EditableTitle`, in the full-size preview). At flush time these are
   * folded on top of `formValues.content_block_profiles` (when present) so
   * an inline edit is never lost even if it happened after the last
   * settings-form snapshot — see `PageDetails.onSave`'s flush for the exact
   * precedence rule and its one known trade-off.
   */
  nameByLanguage: Record<string, string>
  /**
   * Per-language FULL description overrides (already delimiter-joined for
   * multi-section styles) staged from the inline description editor
   * (`EditableDescription` / the multi-section shared Save row). Always
   * flushed via `UPDATE_CONTENT_BLOCK_PROFILE`, the same mutation the old
   * immediate-save `persistDescription` used.
   */
  descriptionByLanguage: Record<string, string>
}

export function emptyBlockDraft(): BlockDraft {
  return { formValues: null, nameByLanguage: {}, descriptionByLanguage: {} }
}

/** Replace the settings-form snapshot wholesale (see `formValues` doc above). */
export function stageFormValues(
  draft: BlockDraft | undefined,
  values: ContentBlockFormValues
): BlockDraft {
  return { ...(draft ?? emptyBlockDraft()), formValues: values }
}

/** Record an inline title edit for one language. */
export function stageName(
  draft: BlockDraft | undefined,
  language: string,
  name: string
): BlockDraft {
  const base = draft ?? emptyBlockDraft()
  return {
    ...base,
    nameByLanguage: { ...base.nameByLanguage, [language]: name },
  }
}

/** Record an inline description edit for one language. */
export function stageDescription(
  draft: BlockDraft | undefined,
  language: string,
  description: string
): BlockDraft {
  const base = draft ?? emptyBlockDraft()
  return {
    ...base,
    descriptionByLanguage: {
      ...base.descriptionByLanguage,
      [language]: description,
    },
  }
}
